import React, { useState, useEffect } from 'react';
import type { MessageType, DateGroupType } from './hooks/useConversations';
import Login from './components/Login';
import Sidebar from './components/Sidebar';
import Main from './components/Main';
import ControlPanel from './components/ControlPanel';
import { useConversations } from './hooks/useConversations';
import './styles/variables.css';
import './styles/globals.css';
import { decryptBytes } from './utils/crypto';

const App: React.FC = () => {
  // Detectar idioma del navegador (ES por defecto; EN si no empieza por 'es')
  const getDefaultLang = (): 'es' | 'en' => {
    if (typeof navigator !== 'undefined') {
      const navLang = navigator.language || (navigator as any).userLanguage;
      return navLang.startsWith('es') ? 'es' : 'en';
    }
    return 'es';
  };
  const [lang, setLang] = useState<'es' | 'en'>(getDefaultLang);
  const [user, setUser] = useState<{ programCode: string | string[]; isAdmin: boolean; token: string } | null>(null);
  const [controlOpen, setControlOpen] = useState(false);
  // Conv para no-admin
  const [convGroups, setConvGroups] = useState<DateGroupType[]>([]);
  const [nonAdminLoading, setNonAdminLoading] = useState(false);
  const [nonAdminSelected, setNonAdminSelected] = useState<string | null>(null);

  // Si es non-admin con un solo código, preseleccionar para cargar conversaciones
  useEffect(() => {
    if (user && !user.isAdmin && !nonAdminSelected) {
      const codes = Array.isArray(user.programCode) ? user.programCode : [user.programCode];
      if (codes.length === 1) {
        setNonAdminSelected(codes[0]);
      }
    }
  }, [user, nonAdminSelected]);

  // Hook de conversaciones para admin
  // Determinar programCode para useConversations de forma segura
  const programCodeStr = user
    ? Array.isArray(user.programCode)
      ? user.programCode[0]
      : user.programCode
    : '';
  
  const {
    activePrograms,
    inactivePrograms,
    selected,
    conversations: adminConvs,
    loadingConversation: adminLoading,
    setSelected,
    addMessageToConversation,
  } = useConversations(
    programCodeStr,
    user?.isAdmin || false
  );

  // Carga de conversaciones non-admin al seleccionar un código de programa
  useEffect(() => {
    if (!user || user.isAdmin || !nonAdminSelected) return;
    setNonAdminLoading(true);
    // URL fija para obtener conversación de asistente non-admin
    const url = `https://headspringchatbot.azurewebsites.net/api/procesar_conversaciones?code=qI6DmJpMP3tLLLnjOy0gXjxIwTKKfJzLogsQKIopL0voAzFuit0o9w==&accion=get_conversacion_asistente&asistente=${nonAdminSelected}`;
    fetch(url)
      .then(async res => {
        const text = await res.text();
        if (!res.ok) throw new Error(`Error HTTP ${res.status}: ${text}`);
        const decrypted = decryptBytes(text, process.env.REACT_APP_FERNET_KEY!);
        return JSON.parse(decrypted);
      })
      .then(data => {
        const byDate: Record<string, MessageType[]> = {};
        Object.entries(data).forEach(([uid, msgs]) => {
          const list = Array.isArray(msgs) ? msgs : (msgs ? [msgs] : []);
          list.forEach(m => {
            const dateValue = m.date;
            const d = new Date(dateValue);
            if (!dateValue || isNaN(d.getTime())) return;
            const msg: MessageType = {
              userId: uid,
              role: m.role,
              date: m.date,
              content: m.content,
              categoria: m.categoria,
              idioma_respuesta: m.idioma_respuesta,
            };
            const key = d.toISOString().slice(0, 10);
            if (!byDate[key]) byDate[key] = [];
            byDate[key].push(msg);
          });
        });
        const dateGroups: DateGroupType[] = Object.keys(byDate)
          .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
          .map(dateKey => ({ date: dateKey, messages: byDate[dateKey] }));
        setConvGroups(dateGroups);
      })
      .catch(err => console.error('Error cargando conversaciones:', err))
      .finally(() => setNonAdminLoading(false));
  }, [nonAdminSelected, user]);

  const handleSendMessage = (userId: string, message: string, date: string) => {
    if (user?.isAdmin) {
      addMessageToConversation(userId, message, date);
    } else {
      const newMessage: MessageType = {
        role: 'admin',
        date: new Date().toISOString(),
        content: message,
        userId,
      };

      const newConvs = [...convGroups];
      let dateGroup = newConvs.find(g => g.date === date);

      if (dateGroup) {
        dateGroup.messages.push(newMessage);
      } else {
        dateGroup = { date, messages: [newMessage] };
        newConvs.unshift(dateGroup);
      }
      
      setConvGroups(newConvs);
    }
  };

  if (!user) {
    return (
      <Login
        onSuccess={(programCode: string, isAdmin: boolean, token: string) => {
          // Parsear el código de programa en un array si viene como JSON
          let codes: string[];
          if (typeof programCode === 'string') {
            try {
              const parsed = JSON.parse(programCode);
              codes = Array.isArray(parsed) ? parsed : [programCode];
            } catch {
              codes = [programCode];
            }
          } else {
            codes = programCode;
          }
          setUser({ programCode: codes, isAdmin, token });
        }}
        currentLang={lang}
        onLangChange={setLang}
      />
    );
  }

  // Vista de panel de control para admin
  if (user.isAdmin && controlOpen) {
    return (
      <ControlPanel
        token={user.token}
        onBack={() => setControlOpen(false)}
        currentLang={lang}
        onLangChange={setLang}
      />
    );
  }

  // Vista admin: sidebar de asistentes y conversaciones completas
  if (user.isAdmin) {
    return (
      <div style={{ display: 'flex', height: '100vh', width: '100vw' }}>
        <Sidebar
          activePrograms={activePrograms}
          inactivePrograms={inactivePrograms}
          onSelect={setSelected}
          currentLang={lang}
        />
        <Main
          selected={selected}
          conversations={adminConvs[selected] || []}
          loadingConversation={adminLoading}
          currentLang={lang}
          onLangChange={setLang}
          isAdmin
          onControl={() => setControlOpen(true)}
          onSendMessage={handleSendMessage}
        />
      </div>
    );
  }

  // Vista no-admin
  const userCodes = Array.isArray(user?.programCode) ? user.programCode : user ? [user.programCode] : [];
  // Si tiene múltiples códigos, mostrar sidebar
  if (userCodes.length > 1) {
    return (
      <div style={{ display: 'flex', height: '100vh', width: '100vw' }}>
        <Sidebar
          activePrograms={userCodes}
          inactivePrograms={[]}
          onSelect={code => setNonAdminSelected(code)}
          currentLang={lang}
        />
        <Main
          selected={nonAdminSelected!}
          conversations={convGroups}
          loadingConversation={nonAdminLoading}
          currentLang={lang}
          onLangChange={setLang}
          onSendMessage={handleSendMessage}
        />
      </div>
    );
  }
  // Si solo un programa, vista full-screen
  return (
    <div style={{ display: 'flex', height: '100vh', width: '100vw' }}>
      <Main
        selected={userCodes[0]}
        conversations={convGroups}
        loadingConversation={nonAdminLoading}
        currentLang={lang}
        onLangChange={setLang}
        onSendMessage={handleSendMessage}
      />
    </div>
  );
};

export default App;
